// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Rxjs import
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// 3rd party imports
import { plainToClass } from 'class-transformer';

// Service imports
import { MPCacheRegistrationService } from '../shared-services/mp-cache-registration-service';

// Model imports
import { CommunitySelectorResultDto } from '../../_models/community-selector-result-dto';
import { CommunitySelectorParameterDto } from '../../_models/community-selector-parameter-dto';
import { SubLedgerSelectorResultDto } from '../../_models/sub-ledger-selector-result-dto';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { CommunityPropertiesResultDto } from '../../community-module/_models/community-properties/community-properties-result-dto';

// Constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

@Injectable({
    providedIn: 'root'
})
export class CommunityService {
    static communitiesMap = new Map<number, CommunitySelectorResultDto>();

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    loadCommunities(clearCache = false): Observable<void> {
        return new Observable((observer) => {
            this.getCommunities(clearCache, true).subscribe(
                (communities) => {
                    CommunityService.communitiesMap = new Map<number, CommunitySelectorResultDto>();
                    communities.forEach((rec) => CommunityService.communitiesMap.set(rec.communityId, rec));
                    observer.next();
                    observer.complete();
                },
                () => {
                    observer.next();
                    observer.complete();
                }
            );
        });
    }

    getCommunities(clearCache = false, includeInactive = false): Observable<CommunitySelectorResultDto[]> {
        const params = new CommunitySelectorParameterDto();
        params.includeInactive = includeInactive;
        params.clearCache = clearCache;
        const url = URLConstants.ccCommunityApiBaseUrl + `selector${params.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, params.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<CommunitySelectorResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(CommunitySelectorResultDto, response)));
    }

    getSubLedgers(clearCache = false): Observable<SubLedgerSelectorResultDto[]> {
        const url = `${URLConstants.ccCommunityCBBaseUrl}/subledgers?clearCache=${clearCache}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get<SubLedgerSelectorResultDto[]>(url, options)
            .pipe(map((response) => plainToClass(SubLedgerSelectorResultDto, response)));
    }

    getCommunitySelectorColumns(): any[] {
        return [
            {
                dataField: 'status',
                dataType: 'string',
                caption: 'Status',
                alignment: 'center',
                cellTemplate: 'statusCellTempalte'
            },
            {
                dataField: 'communityName',
                dataType: 'string',
                caption: 'Community',
                cellTemplate: 'communityCellTemplate',
                allowHeaderFiltering: false
            },
            {
                dataField: 'legalName',
                dataType: 'string',
                caption: 'Legal Name',
                allowHeaderFiltering: false
            },
            {
                dataField: 'locationName',
                dataType: 'string',
                caption: 'Location'
            },
            {
                dataField: 'managementCompanyName',
                dataType: 'string',
                caption: 'Management Company'
            }
        ];
    }

    getCommunityStatusInfo(communityId: any): CommunitySelectorResultDto {
        if (typeof communityId === 'number') {
            return CommunityService.communitiesMap.get(communityId);
        } else {
            return CommunityService.communitiesMap.get(parseInt(communityId));
        }
    }

    getCommunityInfo(communityId: number): Observable<PropertyCommunityInfoResultDto> {
        const url = `${URLConstants.ccOwnerApiBaseUrl}propertycommunityinfo?communityId=${communityId}`;
        this.cacheRegistrationService.registerForCache(url, false);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any) => plainToClass(PropertyCommunityInfoResultDto, response)));
    }

    getCommunityProperties(communityId: number, allOwners = false): Observable<CommunityPropertiesResultDto[]> {
        const url = `${URLConstants.ccCommunityApiBaseUrl}properties?communityId=${communityId}&allOwners=${allOwners}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(CommunityPropertiesResultDto, response)));
    }
}
